import React, { useEffect, useState } from 'react';

import PhoneNumberInput from 'components/PhoneNumberInput';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMessageForInvalidFields, getMessageForRequiredFields } from 'utils/errors';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import FormNavigation from 'components/FormNavigation';
import UserSessionWarning from 'components/Common/UserSessionWarning/UserSessionWarning';
import { useDispatch, useSelector } from 'react-redux';
import { setYourContactData, ContactType } from 'handlers/yourContact';
import { YourContactVariable } from 'enums/LoanFormVariables';
import { RoutePath } from 'enums/Routes';
import { getYourContactData } from 'selectors/getYourContact';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { FlowComponentType } from 'routes/types';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useCurrentFlow from 'hooks/useCurrentFlow';
import { getApplicationStep } from 'selectors/getApplicationStep';
import { useResendLogic } from 'hooks/useResendLogic';
import { getYourNameData } from 'selectors/yourName';
import { getAuthOtpBorrowerFields } from 'handlers/yourName';
import { identityPrefill } from 'thunks';
import { hydratePrefillIntoRedux } from 'utils/hydratePrefillIntoRedux';
import { nextStepAfterPrefill } from 'utils/continueAfterIdentityPrefill';

import styles from './YourPhoneNumber.module.scss';

export const PHONE_NUMBER_LENGTH = 20;

export enum YourContactInputLabel {
  PhoneNumber = 'Phone number',
}
export interface YourPhoneNumberState {
  isDifferentPhoneNumberSession?: boolean;
}

const YourPhoneNumber = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCardFlow } = useCurrentFlow();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const isDifferentPhoneNumberSession = (location.state as YourPhoneNumberState)?.isDifferentPhoneNumberSession;
  const { isTimerActive, canResend, recordSent } = useResendLogic(ContactType.Phone);

  const { phone_number: phoneNumber } = useSelector(getYourContactData);
  const yourNameData = useSelector(getYourNameData);
  const borrowerFields = getAuthOtpBorrowerFields(yourNameData);
  const { completedCustomize } = useSelector(getApplicationStep);

  const [phoneNumberIsValid, setPhoneNumberIsValid] = useState<boolean>(isPossiblePhoneNumber(phoneNumber));
  const [isSendingCode, setIsSendingCode] = useState<boolean>(false);

  const {
    watch,
    register,
    formState: { errors, isValid },
    trigger,
    setValue,
    setError,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      [YourContactVariable.PhoneNumber]: isDifferentPhoneNumberSession ? '' : phoneNumber,
    },
  });

  const watcher = watch();

  const onBlur = () => {
    if (!phoneNumberIsValid) {
      return setError(YourContactVariable.PhoneNumber, {
        message: getMessageForInvalidFields(YourContactInputLabel.PhoneNumber),
      });
    }

    trigger(YourContactVariable.PhoneNumber);
  };

  const onChange = (value: string) => {
    let newIsValid;
    if (value) {
      newIsValid = isPossiblePhoneNumber(value);
    } else {
      newIsValid = false;
    }
    setPhoneNumberIsValid(newIsValid);

    setValue(YourContactVariable.PhoneNumber, value);
    trigger(YourContactVariable.PhoneNumber);
  };

  const handleContinue = async () => {
    const currentPhoneNumber = watcher[YourContactVariable.PhoneNumber]!;
    dispatch(setYourContactData(watcher));

    if (isTimerActive && phoneNumber === currentPhoneNumber) {
      handleNext();
      return;
    }

    if (canResend || phoneNumber !== currentPhoneNumber) {
      setIsSendingCode(true);
      try {
        const response = await dispatchWithUnwrap(
          identityPrefill({
            firstName: borrowerFields.firstName,
            lastName: borrowerFields.lastName,
            phoneNumber: currentPhoneNumber,
          }),
        );
        recordSent(currentPhoneNumber);

        if (response.verified && response.prefill && !response.otpRequired) {
          hydratePrefillIntoRedux(dispatch, response.prefill);
          const nextResult = await nextStepAfterPrefill(
            dispatchWithUnwrap,
            response.sessionToken,
            borrowerFields,
            response.emailPresent,
          );
          handleNext(nextResult);
          return;
        }
      } finally {
        setIsSendingCode(false);
      }
    }

    handleNext();
  };

  useEffect(() => {
    register(YourContactVariable.PhoneNumber, {
      required: getMessageForRequiredFields(YourContactInputLabel.PhoneNumber),
    });
  }, [register]);

  return (
    <>
      <FormNavigation
        {...navigationInfo}
        {...(isCardFlow && { title: 'Sign Up' })}
        {...(completedCustomize && { onBackClick: () => navigate(RoutePath.ExitSurvey) })}
        {...(isDifferentPhoneNumberSession && { onBackClick: () => navigate(RoutePath.YourName) })}
      />
      <FormContainer title="Verify your identity" subtitle="You'll receive an authorization code.">
        <PhoneNumberInput
          label={YourContactInputLabel.PhoneNumber}
          placeholder="000-000-0000"
          errorMessage={errors[YourContactVariable.PhoneNumber]?.message}
          className={styles.formInput}
          name={YourContactVariable.PhoneNumber}
          onChange={onChange}
          onBlur={onBlur}
          value={watcher[YourContactVariable.PhoneNumber]}
          maxLength={PHONE_NUMBER_LENGTH}
          country="US"
          disabled={isSendingCode}
          onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
            e.key === 'Enter' && isValid && phoneNumberIsValid && handleContinue()
          }
          autoFocus={!isDifferentPhoneNumberSession}
        />
        <p className={styles.consentMessage}>
          By entering your number, you're opting in to receive important texts from our customer care team. Msg & data
          rates may apply. Text STOP to cancel anytime.
        </p>

        <Button
          className={styles.button}
          disabled={!isValid || !phoneNumberIsValid}
          isLoading={isSendingCode}
          onClick={handleContinue}
        >
          Send Code
        </Button>
        {isDifferentPhoneNumberSession && (
          <UserSessionWarning
            showContactUsOption
            message="Sorry, it appears the phone number you entered is different from the one you originally provided. Please try again"
          />
        )}
      </FormContainer>
    </>
  );
};

export default YourPhoneNumber;
